import config from "../../config";

export function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export function getToken() {
  const token = `${getCookie("access")}`;
  return token;
}

export function concatPathWithParams(path, queryParams) {
  let pathWithParams = path;
  if (queryParams && Object.keys(queryParams).length) {
    pathWithParams += `?${_queryParamsToQueryString(queryParams)}`;
  }
  return pathWithParams;
}

function _queryParamsToQueryString(queryParams) {
  return Object.keys(queryParams)
    .filter((key) => queryParams[key] !== undefined)
    .map((key) => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join("&");
}

export const parseJwt = (token) => {
  try {
    // JWTs are base64url: atob throws on "-" and "_", which some payloads contain.
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch (e) {
    return null;
  }
};

/** Seconds until a JWT expires, for its cookie's max-age; `fallback` if it can't be read.
 *  The cookies used to get fixed lifetimes (16 h, 24 h) counted from when they were written,
 *  so after a late refresh the access cookie outlived its token by hours: every app on the
 *  shared cookie looked signed in while each call returned 401 (R4). */
function secondsLeft(token, fallback) {
  const exp = parseJwt(token)?.exp;
  return exp ? Math.max(0, exp - Math.floor(Date.now() / 1000)) : fallback;
}

export function setCookie(token) {
  // A login replaces the session rather than merging into it — see clearCookie. Without this,
  // signing in here after another user kept their `${group}=true` cookies, which IMS reads for
  // its permission checks on the same domain.
  clearCookie();

  document.cookie = `refresh=${token.refresh};max-age=${secondsLeft(token.refresh, 86400)};domain=${config.REACT_APP_SECRET_DOMAIN};path=/`;
  setAccessCookie(token.access);
}

/** Write a new access token, and the flags read from it, over the current session.
 *  The start-up refresh uses this rather than setCookie: clearing the jar there also
 *  removed the role cookies the Userport console routes on, so opening this app signed
 *  the user out of the console (R3). The refresh token comes back unchanged, so its
 *  cookie is left alone. */
export function setAccessCookie(access) {
  let manager = parseJwt(access)?.Manager;
  let developer = parseJwt(access)?.Developer;
  const maxAge = secondsLeft(access, 57600);
  document.cookie = `access=${access};max-age=${maxAge};domain=${config.REACT_APP_SECRET_DOMAIN};path=/`;
  if (manager) {
    document.cookie = `manager=${manager};max-age=${maxAge};domain=${config.REACT_APP_SECRET_DOMAIN};path=/`;
  }
  if (developer) {
    document.cookie = `developer=${developer};max-age=${maxAge};domain=${config.REACT_APP_SECRET_DOMAIN};path=/`;
  }
}
export function clearCookie() {
  // Named four cookies and missed every `${group}=true` one Userport writes, so signing out of
  // Field Admin left, e.g., a previous admin's `global_super_user=true` behind for the next user
  // on this browser. Clear the whole jar instead of a list — group names come from the server,
  // so a hardcoded list would silently miss the next one added. Same approach as IMS/Userport.
  const domain = config.REACT_APP_SECRET_DOMAIN;
  const domainAttr = domain && domain !== "undefined" ? `domain=${domain};` : "";
  const EXPIRED = "expires=Thu, 01 Jan 1970 00:00:00 UTC";

  for (const cookie of document.cookie.split(";")) {
    const eqPos = cookie.indexOf("=");
    const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
    if (!name) continue;
    // Both variants: this app writes with a domain attribute, but Userport's cookies on the
    // same host are host-only, and against a real domain those are two distinct cookies that
    // can share a name. Expiring one form would leave the other readable.
    document.cookie = `${name}=;${EXPIRED};path=/`;
    if (domainAttr) document.cookie = `${name}=;${EXPIRED};${domainAttr}path=/`;
  }
  localStorage.removeItem("clientDetails");
}
